// ============================================
// COLECCIONES.JS — Pantalla de Colecciones (ROADMAP D2 Etapa 2)
// ============================================
// Listas CURADAS de productos del catalogo, SIN cliente (a diferencia de las
// Listas pactadas, que son por cliente). Cada producto va a precio NORMAL (se
// calcula al vuelo, igual que La Tienda) o CONGELADO (snapshot fijo del dia).
//
// Backend: src/routes/colecciones.js (todos los endpoints exigen authBearer):
//   GET    /api/colecciones?todas=true
//   GET    /api/colecciones/:id              -> coleccion + items (con precio_actual_mxn)
//   POST   /api/colecciones                  crear
//   PATCH  /api/colecciones/:id              editar (incl. activa)
//   DELETE /api/colecciones/:id              soft delete (activa = false)
//   DELETE /api/colecciones/:id/items/:itemId
//   POST   /api/colecciones/:id/items/:itemId/congelar     congela / re-congela
//   POST   /api/colecciones/:id/items/:itemId/descongelar
//
// Los productos se AGREGAN desde La Tienda (boton "Coleccion" en cada tarjeta).
// Aqui se administran las colecciones y el estado de precio de cada item.
//
// Gating: permiso cotizaciones.colecciones (RutaProtegida lo envuelve desde
// Dashboard.js). Por defecto visible para direccion, administracion y gerente.

import React, { useState, useEffect, useCallback } from 'react';
import * as LucideIcons from 'lucide-react';
import { fetchAuth } from './utils/sesion';
import SelectorIconoLucide, { IconoLucide } from './components/SelectorIconoLucide';
import { API_BASE_CLI } from './components/helpersClientes';
import CarritoFlotante, { useCarrito } from './components/CarritoFlotante';
import BarraAgregandoACotizacion from './components/BarraAgregandoACotizacion';
import { useModoAgregar, leerModoAgregar, salirModoAgregar } from './utils/modoAgregarCotizacion';
import TarjetaProducto from './components/TarjetaProducto';
import SelectorMoneda from './components/SelectorMoneda';
import { useIvaModo, ToggleIva } from './components/SelectorIva';
import ModalRevincular from './components/ModalRevincular';
import { round2, margenDePrecioCosto, InputPrecio, InputMargen } from './components/LineaPrecioEditable';
import { construirAgregadoDesdeItemColeccion } from './utils/carritoOrigen';
import { useAnchoViewport, useResponsive } from './hooks/useResponsive';
import { SkeletonTarjetasColeccion, SkeletonTarjetasProducto } from './components/Skeletons';
import BuscadorInteligente, { useBuscadorInteligente } from './components/BuscadorInteligente';
import FiltroRubro, { useFiltroRubro, AvisoVacioRubro } from './components/FiltroRubro';
import { ChipMargen, AvisoMargenLista, ModalPerdida } from './components/AvisoMargenLista';
import { useUsuario } from './context/UsuarioContext';
import { storage, STORAGE_KEYS } from './utils/storage';

const PP = "'Poppins', sans-serif";

const colores = {
  principal: '#5c9adb',
  oscuro:    '#2f5f9e',
  gris:      '#4e5357',
  grisClaro: '#5e666e',
  verde:     '#5cd4b0',
  rojo:      '#e05c5c',
  amarillo:  '#f5b041',
  fondo:     '#f3f4f4',
  blanco:    '#ffffff',
  borde:     '#e0e3e6',
};

const COLORES_PRESET = ['#93C5FD', '#3B82F6', '#5c9adb', '#5cd4b0', '#e0a05c', '#9a5cdb', '#e05c5c', '#f5b041', '#2f5f9e'];

const FORM_VACIO = { nombre: '', descripcion: '', color: '#5c9adb', icono: 'layers', activa: true };

function fmtFecha(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
}

// Info visual del estado de vigencia del congelado (lo deriva el backend en congelado_estado
// + dias_restantes). Devuelve null si el item no esta congelado.
//  - vigente    -> azul, "Precio congelado"
//  - por_vencer -> ambar, "Vence en N dias"
//  - vencido    -> rojo,  "Vencido hace N dias" (al agregar al carrito usa el precio de hoy)
function infoVigencia(item) {
  const estado = item.congelado_estado;
  if (estado == null) return null;
  const dias = item.dias_restantes;
  if (estado === 'vencido') {
    const n = dias != null ? Math.abs(dias) : null;
    return { estado, vencido: true, color: '#e05c5c', selloBg: 'rgba(224,92,92,0.95)',
      sello: n != null ? `Vencido hace ${n} d${n === 1 ? '' : ''}` : 'Vencido' };
  }
  if (estado === 'por_vencer') {
    return { estado, vencido: false, color: '#c47d12', selloBg: 'rgba(214,150,40,0.97)',
      sello: dias != null ? (dias <= 0 ? 'Vence hoy' : `Vence en ${dias} d`) : 'Por vencer' };
  }
  return { estado, vencido: false, color: '#2f5f9e', selloBg: 'rgba(47,95,158,0.95)', sello: 'Precio congelado' };
}

// ============================================
// RAIZ
// ============================================
export default function Colecciones() {
  const [colecciones, setColecciones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [errorBanner, setErrorBanner] = useState(null);
  const [exitoBanner, setExitoBanner] = useState(null);
  const [detalle, setDetalle] = useState(null); // coleccion abierta en vista detalle
  const [revincularAbierto, setRevincularAbierto] = useState(false); // herramienta re-vincular

  // Modal: 'crear' | 'editar' | null
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [editId, setEditId] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [errorModal, setErrorModal] = useState(null);

  // useCallback: referencia estable para no provocar re-renders en cascada en los hijos
  // (DetalleColeccion los recibe como onExito/onError). Ver fix del bucle de recarga.
  const flashExito = useCallback((msg) => {
    setExitoBanner(msg);
    setTimeout(() => setExitoBanner(null), 3500);
  }, []);
  const flashError = useCallback((msg) => {
    setErrorBanner(msg);
    setTimeout(() => setErrorBanner(null), 6000);
  }, []);

  const cargar = useCallback(() => {
    setCargando(true);
    // congeladas=false: esta pantalla muestra SOLO colecciones normales (catalogo).
    // Las listas CONGELADAS viven en "Listas pactadas" (seccion Listas congeladas).
    fetchAuth(`${API_BASE_CLI}/api/colecciones?todas=true&congeladas=false`)
      .then(r => r.json())
      .then(d => { if (d.ok) setColecciones(d.colecciones || []); else setErrorBanner(d.error || 'Error al cargar'); })
      .catch(e => { if (e.code !== 'SESION_EXPIRADA') setErrorBanner('Error al cargar: ' + (e.message || '')); })
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // ALERTA-BAJO-COSTO: si se llega desde el aviso del vigilante, abrir ESA coleccion directamente.
  useEffect(() => {
    const pendiente = storage.get(STORAGE_KEYS.LISTA_ABRIR);
    if (pendiente && pendiente.id != null && pendiente.congelada === false) {
      storage.remove(STORAGE_KEYS.LISTA_ABRIR);
      setDetalle({ id: Number(pendiente.id), nombre: pendiente.nombre || '' });
    }
  }, []);

  // ============================================
  // MODAL crear / editar
  // ============================================
  const abrirCrear = () => {
    setForm(FORM_VACIO);
    setEditId(null);
    setErrorModal(null);
    setModal('crear');
  };
  const abrirEditar = (c) => {
    setForm({
      nombre: c.nombre || '',
      descripcion: c.descripcion || '',
      color: c.color || '#5c9adb',
      icono: c.icono || 'layers',
      activa: c.activa !== false,
    });
    setEditId(c.id);
    setErrorModal(null);
    setModal('editar');
  };
  const cerrarModal = () => {
    setModal(null);
    setForm(FORM_VACIO);
    setEditId(null);
    setErrorModal(null);
  };

  const guardar = async () => {
    const nombre = (form.nombre || '').trim();
    if (!nombre) { setErrorModal('El nombre es obligatorio.'); return; }
    const color = (form.color || '').trim();
    if (color && !/^#[0-9A-Fa-f]{6}$/.test(color)) {
      setErrorModal('El color debe ser un hex tipo #RRGGBB.');
      return;
    }
    setGuardando(true);
    setErrorModal(null);
    try {
      const body = {
        nombre,
        descripcion: (form.descripcion || '').trim() || null,
        color: color || null,
        icono: (form.icono || '').trim() || null,
        activa: !!form.activa,
      };
      const url = modal === 'crear'
        ? `${API_BASE_CLI}/api/colecciones`
        : `${API_BASE_CLI}/api/colecciones/${editId}`;
      const resp = await fetchAuth(url, {
        method: modal === 'crear' ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok || !d.ok) {
        setErrorModal(d.error || 'No se pudo guardar la coleccion.');
        setGuardando(false);
        return;
      }
      flashExito(modal === 'crear' ? 'Coleccion creada.' : 'Coleccion actualizada.');
      cerrarModal();
      cargar();
    } catch (e) {
      if (e.code !== 'SESION_EXPIRADA') setErrorModal('Error al guardar: ' + (e.message || ''));
    } finally {
      setGuardando(false);
    }
  };

  // Borrar = desactivar (soft)
  const desactivar = async (c) => {
    if (!window.confirm(`Desactivar la coleccion "${c.nombre}"? Dejara de mostrarse. Podras reactivarla editandola.`)) return;
    try {
      const resp = await fetchAuth(`${API_BASE_CLI}/api/colecciones/${c.id}`, { method: 'DELETE' });
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok || !d.ok) { flashError(d.error || 'No se pudo desactivar.'); return; }
      flashExito('Coleccion desactivada.');
      cargar();
    } catch (e) {
      if (e.code !== 'SESION_EXPIRADA') flashError('Error: ' + (e.message || ''));
    }
  };

  // ============================================
  // VISTA DETALLE
  // ============================================
  if (detalle) {
    return (
      <DetalleColeccion
        coleccionBase={detalle}
        onVolver={() => { setDetalle(null); cargar(); }}
        onExito={flashExito}
        onError={flashError}
      />
    );
  }

  // ============================================
  // VISTA LISTA
  // ============================================
  return (
    <div style={{ fontFamily: PP, color: colores.gris, padding: '4px 2px' }}>
      {/* F3.B: barra "Agregando a la cotizacion X" (se auto-oculta si no hay modo). */}
      <BarraAgregandoACotizacion />
      {/* Cabecera */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 6 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, color: colores.oscuro, fontWeight: 700 }}>Colecciones</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: colores.grisClaro, maxWidth: 620, lineHeight: 1.5 }}>
            Listas curadas de productos del catalogo (sin cliente). Cada producto puede ir a precio
            normal (varia con el tipo de cambio) o congelado (precio fijo del dia). Los productos se
            agregan desde La Tienda.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setRevincularAbierto(true)} style={estiloBotonSecundario} title="Corregir productos escaneados que perdieron su vinculo al catalogo">
            <LucideIcons.Link2 size={16} /> Re-vincular productos
          </button>
          <button onClick={abrirCrear} style={estiloBotonPrimarioGrande}>
            <LucideIcons.Plus size={16} strokeWidth={2.5} /> Nueva coleccion
          </button>
        </div>
      </div>

      {errorBanner && <div style={bannerError}>{errorBanner}</div>}
      {exitoBanner && <div style={bannerExito}>{exitoBanner}</div>}

      {cargando ? (
        <div style={{ marginTop: 14 }}><SkeletonTarjetasColeccion /></div>
      ) : colecciones.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: colores.grisClaro, background: colores.blanco, border: `1px solid ${colores.borde}`, borderRadius: 12, marginTop: 12 }}>
          Sin colecciones todavia. Crea la primera con <strong>Nueva coleccion</strong>.
        </div>
      ) : (
        <div style={{
          display: 'grid', gap: 12, marginTop: 14,
          // min(100%, 280px): en desktop = 280 (igual que hoy); en celular <280px no desborda.
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
        }}>
          {colecciones.map(c => (
            <TarjetaColeccion
              key={c.id}
              coleccion={c}
              onAbrir={() => setDetalle(c)}
              onEditar={() => abrirEditar(c)}
              onDesactivar={() => desactivar(c)}
            />
          ))}
        </div>
      )}

      {/* Modal crear / editar */}
      {modal && (
        <ModalOverlay onClose={cerrarModal}>
          <h3 style={{ margin: '0 0 16px', fontSize: 18, color: colores.oscuro, fontWeight: 700 }}>
            {modal === 'crear' ? 'Nueva coleccion' : 'Editar coleccion'}
          </h3>

          <label style={estiloLabel}>Nombre</label>
          <input
            value={form.nombre}
            onChange={ev => setForm(f => ({ ...f, nombre: ev.target.value }))}
            placeholder="Ej: Kit CCTV recomendado"
            style={estiloInput}
          />

          <label style={estiloLabel}>Descripcion <span style={{ color: colores.grisClaro, fontWeight: 400 }}>(opcional)</span></label>
          <textarea
            value={form.descripcion}
            onChange={ev => setForm(f => ({ ...f, descripcion: ev.target.value }))}
            placeholder="Para que sirve esta coleccion"
            style={{ ...estiloInput, minHeight: 60, resize: 'vertical' }}
          />

          <label style={estiloLabel}>Color</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{
              width: 34, height: 34, borderRadius: 8, flexShrink: 0,
              background: form.color || colores.grisClaro,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              border: `1px solid ${colores.borde}`,
            }}>
              <IconoLucide nombre={form.icono} size={18} color="#ffffff" />
            </span>
            <input
              value={form.color}
              onChange={ev => setForm(f => ({ ...f, color: ev.target.value }))}
              placeholder="#5c9adb"
              style={{ ...estiloInput, marginBottom: 0, flex: 1 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            {COLORES_PRESET.map(c => (
              <button
                key={c}
                onClick={() => setForm(f => ({ ...f, color: c }))}
                title={c}
                style={{ width: 22, height: 22, borderRadius: 6, background: c, cursor: 'pointer', border: form.color === c ? `2px solid ${colores.oscuro}` : `1px solid ${colores.borde}` }}
              />
            ))}
          </div>

          <label style={estiloLabel}>Icono</label>
          <div style={{ marginBottom: 14 }}>
            <SelectorIconoLucide value={form.icono} onChange={(nombre) => setForm(f => ({ ...f, icono: nombre }))} />
          </div>

          <label style={{ ...estiloLabel, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={!!form.activa}
              onChange={ev => setForm(f => ({ ...f, activa: ev.target.checked }))}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            Activa (visible)
          </label>

          {errorModal && <div style={{ ...bannerError, marginTop: 12, marginBottom: 0 }}>{errorModal}</div>}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button onClick={cerrarModal} disabled={guardando} style={estiloBotonSecundario}>Cancelar</button>
            <button onClick={guardar} disabled={guardando} style={estiloBotonPrimario}>
              {guardando ? 'Guardando...' : (modal === 'crear' ? 'Crear coleccion' : 'Guardar cambios')}
            </button>
          </div>
        </ModalOverlay>
      )}

      {revincularAbierto && (
        <ModalRevincular
          onCerrar={() => { setRevincularAbierto(false); cargar(); }}
          onCambio={() => { /* recarga al cerrar; el conteo de items_congelados se refresca */ }}
        />
      )}

    </div>
  );
}

// ============================================
// Tarjeta de coleccion (vista lista)
// ============================================
function TarjetaColeccion({ coleccion, onAbrir, onEditar, onDesactivar }) {
  const color = coleccion.color || colores.principal;
  const total = coleccion.items_total || 0;
  const congelados = coleccion.items_congelados || 0;
  return (
    <div style={{
      background: colores.blanco, border: `1px solid ${colores.borde}`,
      borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
      borderLeft: `4px solid ${color}`, opacity: coleccion.activa === false ? 0.6 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span style={{
          width: 42, height: 42, borderRadius: 10, flexShrink: 0,
          background: color + '22', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <IconoLucide nombre={coleccion.icono} size={22} color={color} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: colores.oscuro, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {coleccion.nombre}
            {coleccion.activa === false && (
              <span style={{ fontSize: 11, fontWeight: 600, color: colores.grisClaro, background: colores.fondo, padding: '2px 7px', borderRadius: 20 }}>
                Inactiva
              </span>
            )}
          </div>
          {coleccion.descripcion && (
            <div style={{ fontSize: 12, color: colores.grisClaro, marginTop: 3, lineHeight: 1.4 }}>
              {coleccion.descripcion}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 12, color: colores.gris }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <LucideIcons.Package size={14} color={colores.grisClaro} />
          <strong style={{ color: colores.oscuro }}>{total}</strong> producto{total === 1 ? '' : 's'}
        </span>
        {congelados > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: colores.oscuro }}>
            <LucideIcons.Snowflake size={14} color={colores.principal} />
            {congelados} congelado{congelados === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
        <button onClick={onAbrir} style={{ ...estiloBotonPrimario, flex: 1, justifyContent: 'center' }}>
          Ver productos
        </button>
        <button onClick={onEditar} title="Editar" style={estiloBotonIcono}>
          <LucideIcons.Pencil size={16} />
        </button>
        {coleccion.activa !== false && (
          <button onClick={onDesactivar} title="Desactivar" style={{ ...estiloBotonIcono, color: colores.rojo }}>
            <LucideIcons.Trash2 size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================
// VISTA DETALLE — productos de una coleccion
// ============================================
export function DetalleColeccion({ coleccionBase, onVolver, onExito, onError }) {
  const [coleccion, setColeccion] = useState(coleccionBase);
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [ocupado, setOcupado] = useState(null); // id de item con accion en curso
  const [agregandoCarrito, setAgregandoCarrito] = useState(null); // id de item agregandose al carrito
  const [mostrarBuscador, setMostrarBuscador] = useState(false); // buscador embebido para agregar
  const [vista, setVista] = useState('tarjetas'); // 'tarjetas' | 'tabla'
  const [guardandoTabla, setGuardandoTabla] = useState(false);
  // ALERTA-BAJO-COSTO (2026-10-13): resumen del margen REAL (lo calcula el servidor contra el
  // costo de hoy), filtro del aviso de arriba y la ventana de la perdida. Quien no ve costos
  // recibe el resumen en null (mascara del servidor) y no se pinta nada.
  const [margenResumen, setMargenResumen] = useState(null);
  const [filtroMargen, setFiltroMargen] = useState(null);
  const [itemPerdida, setItemPerdida] = useState(null);
  const [guardandoPerdida, setGuardandoPerdida] = useState(false);
  const [errorPerdida, setErrorPerdida] = useState(null);
  // Marcar una perdida es de DIRECCION (bypass de acceso total; el candado real esta en el servidor).
  const { accesoTotal } = useUsuario();
  // Mismo carrito que La Tienda (backend). Lo usamos para agregar productos de la
  // coleccion respetando su precio congelado.
  const { agregarItem } = useCarrito();

  const cargar = useCallback(() => {
    setCargando(true);
    fetchAuth(`${API_BASE_CLI}/api/colecciones/${coleccionBase.id}`)
      .then(r => r.json())
      .then(d => {
        if (d.ok) { setColeccion(d.coleccion || coleccionBase); setItems(d.items || []); setMargenResumen(d.margen_resumen || null); }
        else onError(d.error || 'Error al cargar la coleccion');
      })
      .catch(e => { if (e.code !== 'SESION_EXPIRADA') onError('Error: ' + (e.message || '')); })
      .finally(() => setCargando(false));
    // FIX bucle de recarga: depende SOLO del id estable de la coleccion. onError es un
    // callback del padre que cambia de referencia en cada re-render (toast); si estuviera
    // aqui, el useEffect re-disparaba el fetch en cada interaccion -> la lista se "recargaba
    // sola". onError solo se usa en el catch (mostrar aviso), no debe re-disparar la carga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coleccionBase.id]);

  useEffect(() => { cargar(); }, [cargar]);

  const accionItem = async (item, accion, extra = {}, msgExito = null) => {
    setOcupado(item.id);
    try {
      let resp;
      if (accion === 'quitar') {
        if (!window.confirm(`Quitar "${item.nombre}" de la coleccion?`)) { setOcupado(null); return; }
        resp = await fetchAuth(`${API_BASE_CLI}/api/colecciones/${coleccion.id}/items/${item.id}`, { method: 'DELETE' });
      } else {
        // 'congelar' (tambien re-programar) o 'descongelar'. En congelar puede viajar
        // precio_manual_mxn / dias_vigencia / mantener_precio.
        resp = await fetchAuth(`${API_BASE_CLI}/api/colecciones/${coleccion.id}/items/${item.id}/${accion}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(extra || {}),
        });
      }
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok || !d.ok) { onError(d.error || 'No se pudo completar la accion.'); setOcupado(null); return; }
      const msg = msgExito || (accion === 'quitar' ? 'Producto quitado.'
        : accion === 'descongelar' ? 'Precio descongelado.'
        : 'Precio congelado.');
      onExito(msg);
      cargar();
    } catch (e) {
      if (e.code !== 'SESION_EXPIRADA') onError('Error: ' + (e.message || ''));
    } finally {
      setOcupado(null);
    }
  };

  // ALERTA-BAJO-COSTO: marcar / quitar "a perdida, aceptada" (solo direccion, con motivo).
  const accionPerdida = async (metodo, motivo) => {
    if (!itemPerdida) return;
    setGuardandoPerdida(true); setErrorPerdida(null);
    try {
      const resp = await fetchAuth(`${API_BASE_CLI}/api/colecciones/${coleccion.id}/items/${itemPerdida.id}/perdida-aceptada`, {
        method: metodo,
        headers: { 'Content-Type': 'application/json' },
        body: metodo === 'POST' ? JSON.stringify({ motivo }) : undefined,
      });
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok || !d.ok) { setErrorPerdida(d.error || 'No se pudo guardar.'); return; }
      onExito(metodo === 'POST' ? 'Marcada como pérdida aceptada.' : 'Marca quitada: la línea vuelve a avisar.');
      setItemPerdida(null);
      cargar();
    } catch (e) {
      if (e.code !== 'SESION_EXPIRADA') setErrorPerdida('Error: ' + (e.message || ''));
    } finally {
      setGuardandoPerdida(false);
    }
  };

  // Agregar un item de la coleccion. Si el item esta CONGELADO, manda el precio congelado
  // (+ snapshot de costo/margen/origen) como precio forzado para que NO se recalcule el de
  // hoy. Si no, agrega normal (precio de hoy). `prod` viene de la tarjeta de tienda: para
  // productos variables NO congelados trae el referencia_id con la variante elegida
  // (ej. "5:12"); se respeta esa seleccion.
  // F3.D: BIFURCA segun el "modo agregar a cotizacion":
  //  - HAY modo  -> POST directo a la cotizacion (sin carrito), via el endpoint de F3.A.
  //  - NO hay modo -> carrito normal (flujo de antes, INTACTO).
  // Devuelve true si se agrego (para el feedback visual del boton).
  const agregarAlCarrito = async (item, cantidad, prod = null) => {
    setAgregandoCarrito(item.id);
    try {
      // REGLA DE DINERO compartida (utils/carritoOrigen): congelado vigente -> precio forzado +
      // snapshot; vencido/no congelado -> precio de hoy + variante. Mismo comportamiento de antes.
      const { tipo, referencia_id, extra, congeladoVigente } = construirAgregadoDesdeItemColeccion(item, prod);
      const etiquetaPrecio = congeladoVigente ? 'precio congelado'
        : (item.congelado_estado === 'vencido' ? 'precio de hoy (congelado vencido)' : 'precio de hoy');

      // F3.D: en "modo agregar a cotizacion" el item va DIRECTO a la cotizacion. El ...extra
      // lleva precio_forzado_mxn + snapshots cuando el congelado esta VIGENTE (AMBAR: respeta
      // el precio pactado); si vencido/no congelado, extra={} -> precio de hoy (igual que el
      // carrito). La regla la da el helper, no se inventa aqui.
      const modo = leerModoAgregar();
      if (modo) {
        // Mejora #4b: si el modo es SUSTITUIR -> reemplaza la linea (PUT .../sustituir) con el
        // MISMO body (incluye ...extra -> precio_forzado del congelado vigente), sale del modo y
        // vuelve al cotizador. Si no, append normal (F3.D, intacto).
        if (modo.sustituirLineaId != null) {
          const resp = await fetchAuth(`${API_BASE_CLI}/api/cotizaciones/${modo.cotizacionId}/productos/${modo.sustituirLineaId}/sustituir`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cliente_id: null, tipo, referencia_id, ...extra }),
          });
          const d = await resp.json().catch(() => ({}));
          if (!resp.ok || !d.ok) { onError(d.error || 'No se pudo sustituir.'); return false; }
          onExito(`Producto sustituido: ${item.nombre} (${etiquetaPrecio}).`);
          salirModoAgregar();
          try { window.dispatchEvent(new CustomEvent('beep-editar-cotizacion', { detail: { cotizacionId: modo.cotizacionId } })); } catch { /* none */ }
          return true;
        }
        const resp = await fetchAuth(`${API_BASE_CLI}/api/cotizaciones/${modo.cotizacionId}/productos`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cliente_id: null, // Colecciones es catalogo SIN cliente
            items: [{ tipo, referencia_id, cantidad, ...extra }],
          }),
        });
        const d = await resp.json().catch(() => ({}));
        if (!resp.ok || !d.ok) { onError(d.error || 'No se pudo agregar a la cotizacion.'); return false; }
        onExito(`Agregado a la cotizacion: ${item.nombre} (${etiquetaPrecio}).`);
        // F3.F: avisa a la barra para refrescar el contador "Llevas N" al instante.
        try { window.dispatchEvent(new CustomEvent('beep-agregado-a-cotizacion')); } catch { /* none */ }
        return true; // NO sale del modo: se pueden agregar varios seguidos hasta pulsar "Listo".
      }

      // Sin modo: carrito normal (INTACTO).
      await agregarItem({ tipo, referencia_id }, cantidad, extra);
      onExito(`Agregado al carrito: ${cantidad} x ${item.nombre} (${etiquetaPrecio}).`);
      return true;
    } catch (e) {
      if (e.code !== 'SESION_EXPIRADA') onError('No se pudo agregar: ' + (e.message || ''));
      return false;
    } finally {
      setAgregandoCarrito(null);
    }
  };

  // Re-programar un item: el mini-modal de la tarjeta elige precio (mantener/actualizar)
  // + nueva vigencia (dias). Aqui solo se dispara el POST congelar con esas opciones y un
  // toast claro con la fecha de vencimiento resultante.
  const reprogramar = (item, opts) => {
    const dias = Number(opts && opts.dias_vigencia) || 0;
    const f = new Date(); f.setDate(f.getDate() + dias);
    const fechaTxt = f.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
    const precioTxt = opts && opts.mantener_precio ? 'precio mantenido' : 'precio actualizado a hoy';
    return accionItem(item, 'congelar', opts, `Re-programado: vence el ${fechaTxt} (${precioTxt}).`);
  };

  // VARIANTES Fase 2: persiste la variante elegida del item (PATCH). Para NO congelados el
  // precio/costo se recalcula solo; para CONGELADOS el precio pactado se mantiene (avisa).
  const guardarVariante = async (item, varianteId) => {
    setOcupado(item.id);
    try {
      const resp = await fetchAuth(`${API_BASE_CLI}/api/colecciones/${coleccion.id}/items/${item.id}/variante`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variante_id: Number(varianteId) }),
      });
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok || !d.ok) { onError(d.error || 'No se pudo guardar la variante.'); setOcupado(null); return; }
      onExito(d.mensaje || 'Variante actualizada.');
      cargar();
    } catch (e) {
      if (e.code !== 'SESION_EXPIRADA') onError('Error: ' + (e.message || ''));
    } finally {
      setOcupado(null);
    }
  };

  // GUARDAR (vista tabla): persiste las lineas EDITADAS congelando el precio/margen exacto
  // (solo_precio: no recalcula con el cache de hoy ni toca la vigencia). Una llamada por
  // linea cambiada, secuencial (N chico). Devuelve cuantas se guardaron.
  const guardarTabla = async (cambios) => {
    if (!cambios || cambios.length === 0) { onExito('No hay cambios que guardar.'); return; }
    setGuardandoTabla(true);
    let okN = 0;
    try {
      for (const c of cambios) {
        const resp = await fetchAuth(`${API_BASE_CLI}/api/colecciones/${coleccion.id}/items/${c.id}/congelar`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            solo_precio: true,
            precio_manual_mxn: c.precio,
            margen_snapshot_pct: c.margen,
            costo_snapshot_mxn: c.costo,
          }),
        });
        const d = await resp.json().catch(() => ({}));
        if (resp.ok && d.ok) okN++;
        else onError(`No se pudo guardar "${c.nombre}": ${d.error || 'error'}`);
      }
      if (okN > 0) onExito(`Guardado: ${okN} producto${okN === 1 ? '' : 's'} con precio congelado.`);
      cargar();
    } catch (e) {
      if (e.code !== 'SESION_EXPIRADA') onError('Error al guardar: ' + (e.message || ''));
    } finally {
      setGuardandoTabla(false);
    }
  };

  // Contadores de vencimiento para el aviso a nivel lista.
  const porVencer = items.filter(i => i.congelado_estado === 'por_vencer').length;
  const vencidos = items.filter(i => i.congelado_estado === 'vencido').length;

  const color = coleccion.color || colores.principal;
  // F3.B: en "modo agregar a cotizacion" se oculta el carrito (independiente del carrito).
  const modoAgregar = useModoAgregar();
  // RESPONSIVE A-3: en celular/tablet el toggle + "Agregar productos" caen a su propia fila
  // debajo del titulo (sin amontonarse). En desktop ancho, igual que hoy.
  const { isMobile, isTablet } = useResponsive();
  const cabeceraCompacta = isMobile || isTablet;
  // Switches globales Con/Sin IVA + MXN/USD (mismos helpers que La Tienda). Las tarjetas
  // (TarjetaProducto via TarjetaItemColeccion) ya leen ivaModo y la moneda por su cuenta;
  // estos controles solo exponen el alternador en la cabecera del detalle.
  const [ivaModo, setIvaModo] = useIvaModo();
  // PRECIO-TARJETAS (2026-10-12): UNA sola edicion de precios para las dos vistas. Lo que se edita
  // en Tarjetas aparece en Tabla y viceversa, y "Guardar" usa el mismo camino (guardarTabla).
  const edicion = useEdicionPrecios(items);
  // BUSCADOR-UNICO (2026-10-12): el motor de La Tienda sobre los productos de ESTA lista.
  const busq = useBuscadorInteligente(items, candidatoDeItem);
  // RUBROS (2026-10-16): el filtro por rubro va ENCIMA de lo que el buscador dejo (los dos conviven).
  const rubro = useFiltroRubro(busq.visibles);
  const filasPorId = new Map(edicion.filas.map(f => [f.id, f]));
  // ALERTA-BAJO-COSTO: el aviso de arriba filtra la lista a las lineas de ese estado.
  const visibles = filtroMargen
    ? rubro.visibles.filter(it => it.margen_estado && it.margen_estado.estado === filtroMargen)
    : rubro.visibles;

  return (
    <div style={{ fontFamily: PP, color: colores.gris, padding: '4px 2px' }}>
      {/* F3.B: barra "Agregando a la cotizacion X" (se auto-oculta si no hay modo). */}
      <BarraAgregandoACotizacion />
      {/* Cabecera */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <button onClick={onVolver} style={estiloBotonSecundario}>
          <LucideIcons.ArrowLeft size={15} /> Volver
        </button>
        <span style={{
          width: 38, height: 38, borderRadius: 9, flexShrink: 0,
          background: color + '22', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <IconoLucide nombre={coleccion.icono} size={20} color={color} />
        </span>
        <div style={{ flex: 1, minWidth: cabeceraCompacta ? 120 : 200 }}>
          <h2 style={{ margin: 0, fontSize: 20, color: colores.oscuro, fontWeight: 700 }}>{coleccion.nombre}</h2>
          <div style={{ fontSize: 12, color: colores.grisClaro }}>
            {items.length} producto{items.length === 1 ? '' : 's'} en la lista
            {coleccion.congelada && coleccion.congelado_dias ? ` · vigencia ${coleccion.congelado_dias} dias` : ''}
          </div>
        </div>
        {/* Toggle de vista + "Agregar productos": en compacto caen a su propia fila (flexBasis
            100%, espaciados), sin amontonarse contra el titulo. En desktop, igual que hoy. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          ...(cabeceraCompacta ? { flexBasis: '100%', justifyContent: 'space-between' } : {}),
        }}>
          {/* Switches Con/Sin IVA + MXN/USD (mismos que La Tienda), sobre los precios
              congelados/fijos. Las tarjetas reaccionan al estado global al instante. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <ToggleIva modo={ivaModo} onCambio={setIvaModo} compacto />
            <SelectorMoneda compacto />
          </div>
          {/* Toggle de vista: tarjetas (tienda) | tabla (cotizador editable). */}
          <div style={{ display: 'inline-flex', border: `1px solid ${colores.borde}`, borderRadius: 8, overflow: 'hidden' }}>
            {[
              { id: 'tarjetas', label: 'Tarjetas', icono: LucideIcons.LayoutGrid },
              { id: 'tabla', label: 'Tabla', icono: LucideIcons.Table },
            ].map(v => (
              <button key={v.id} onClick={() => setVista(v.id)} title={`Vista ${v.label}`} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px',
                border: 'none', cursor: 'pointer', fontFamily: PP, fontSize: 13, fontWeight: 600,
                background: vista === v.id ? colores.principal : colores.blanco,
                color: vista === v.id ? colores.blanco : colores.gris,
              }}>
                <v.icono size={15} /> {v.label}
              </button>
            ))}
          </div>
          <button onClick={() => setMostrarBuscador(v => !v)} style={estiloBotonPrimario}>
            <LucideIcons.Plus size={16} strokeWidth={2.5} /> Agregar productos
          </button>
        </div>
      </div>

      {/* Aviso a nivel lista: items por vencer / vencidos. */}
      {(porVencer > 0 || vencidos > 0) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
          padding: '10px 14px', borderRadius: 10, marginBottom: 12,
          background: vencidos > 0 ? '#fdecec' : '#fff7e8',
          border: `1px solid ${vencidos > 0 ? '#f5b7b7' : '#f0d59a'}`,
          fontSize: 13, color: colores.gris,
        }}>
          <LucideIcons.AlertTriangle size={18} color={vencidos > 0 ? colores.rojo : '#c47d12'} />
          {vencidos > 0 && <span><strong style={{ color: colores.rojo }}>{vencidos}</strong> producto{vencidos === 1 ? '' : 's'} con precio <strong>vencido</strong> (al agregar usa el precio de hoy)</span>}
          {vencidos > 0 && porVencer > 0 && <span style={{ color: colores.grisClaro }}>·</span>}
          {porVencer > 0 && <span><strong style={{ color: '#c47d12' }}>{porVencer}</strong> por vencer pronto</span>}
        </div>
      )}

      {/* ALERTA-BAJO-COSTO: lo que se vende bajo el costo de HOY, separado de lo ya decidido. */}
      <AvisoMargenLista resumen={margenResumen} filtro={filtroMargen} onFiltro={setFiltroMargen} />

      {/* Buscador embebido para AGREGAR productos a la lista (reusa /api/tienda/buscar). */}
      {mostrarBuscador && (
        <BuscadorAgregar
          coleccion={coleccion}
          onAgregado={(nombre) => { onExito(`Agregado a la lista: ${nombre}.`); cargar(); }}
          onError={onError}
          onCerrar={() => setMostrarBuscador(false)}
        />
      )}

      {cargando ? (
        <SkeletonTarjetasProducto />
      ) : items.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: colores.grisClaro, background: colores.blanco, border: `1px solid ${colores.borde}`, borderRadius: 12 }}>
          Esta lista no tiene productos todavia. Usa <strong>Agregar productos</strong> aqui arriba, o desde <strong>La Tienda</strong> con el boton <strong>Coleccion</strong> de cada producto.
        </div>
      ) : (
        <>
        <BuscadorInteligente busq={busq} placeholder="Buscar en esta lista (nombre, marca o modelo)" />
        <FiltroRubro filtro={rubro} />
        {vista === 'tabla' && <BarraCambiosPrecio edicion={edicion} guardando={guardandoTabla} onGuardar={guardarTabla} />}
        {/* RUBROS: nunca pantalla en blanco; si el rubro elegido queda en cero, se dice con texto. */}
        {visibles.length === 0 ? (rubro.activo
          ? <AvisoVacioRubro filtro={rubro} busqueda={busq.q} />
          : filtroMargen
            ? <div style={{ padding: '18px 14px', textAlign: 'center', fontSize: 13, color: colores.grisClaro, background: colores.blanco, border: `1px dashed ${colores.borde}`, borderRadius: 12 }}>Ninguna linea de esta lista esta en ese estado de margen. <button type="button" onClick={() => setFiltroMargen(null)} style={{ border: 'none', background: 'none', padding: 0, color: colores.principal, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, textDecoration: 'underline' }}>Ver todas</button></div>
            : null) : vista === 'tabla' ? (
        <VistaTablaColeccion
          edicion={edicion}
          idsOrden={visibles.map(it => it.id)}
          onMargenClick={(it) => { setErrorPerdida(null); setItemPerdida(it); }}
          onQuitar={(it) => accionItem(it, 'quitar')}
          onAgregarCarrito={(it, cant) => agregarAlCarrito(it, cant)}
          agregandoCarritoId={agregandoCarrito}
          modoCotizacion={!!modoAgregar}
        />
      ) : (
        <div style={{
          display: 'grid', gap: 16,
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 230px), 1fr))',
        }}>
          {visibles.map(it => (
            <TarjetaItemColeccion
              key={it.id}
              item={it}
              onMargenClick={() => { setErrorPerdida(null); setItemPerdida(it); }}
              fila={filasPorId.get(it.id)}
              onPrecio={edicion.onPrecio}
              onMargen={edicion.onMargen}
              ocupado={ocupado === it.id}
              agregandoCarrito={agregandoCarrito === it.id}
              modoCotizacion={!!modoAgregar}
              onAgregarCarrito={(cant, prod) => agregarAlCarrito(it, cant, prod)}
              onQuitar={() => accionItem(it, 'quitar')}
              onReprogramar={(opts) => reprogramar(it, opts)}
              onGuardarVariante={(varId) => guardarVariante(it, varId)}
            />
          ))}
        </div>
        )}
        {/* En Tarjetas la barra solo sale con cambios, y PEGADA abajo: se edita un producto de mas
            abajo y Guardar sigue a la vista. */}
        {vista !== 'tabla' && edicion.hayCambios && (
          <BarraCambiosPrecio edicion={edicion} guardando={guardandoTabla} onGuardar={guardarTabla} flotante />
        )}
        </>
      )}

      {/* Mismo carrito flotante que La Tienda: el contador se actualiza al agregar y
          permite abrir el panel / generar cotizacion sin salir de Colecciones. */}
      {/* F3.B: el carrito se OCULTA mientras se agrega a una cotizacion. */}
      {!modoAgregar && <CarritoFlotante />}

      {itemPerdida && (
        <ModalPerdida
          item={itemPerdida}
          puedeMarcar={!!accesoTotal}
          guardando={guardandoPerdida}
          error={errorPerdida}
          onCerrar={() => setItemPerdida(null)}
          onGuardar={(motivo) => accionPerdida('POST', motivo)}
          onQuitar={() => accionPerdida('DELETE')}
        />
      )}
    </div>
  );
}

// ============================================
// Tarjeta de producto dentro del detalle — MISMA tarjeta de La Tienda
// ============================================
// Reusa TarjetaProducto (foto/precio/specs) para que el detalle se vea como una
// extension de La Tienda. Si el item esta CONGELADO, el backend ya manda el producto
// con el precio congelado y aqui se pinta un SELLO "Precio congelado ❄". Las acciones
// de tienda que no aplican (Lista/Coleccion/Compartir) no se pasan. Se conserva el
// stepper de cantidad + Agregar (en la tarjeta) y se agrega "Quitar de la lista".
function TarjetaItemColeccion({ item, fila, onPrecio, onMargen, onMargenClick, ocupado, agregandoCarrito, modoCotizacion = false, onAgregarCarrito, onQuitar, onReprogramar, onGuardarVariante }) {
  const congelado = item.congelado === true;
  const vig = infoVigencia(item); // null si no congelado
  const [reprogAbierto, setReprogAbierto] = useState(false);

  // Producto en shape de tarjeta de tienda (lo arma el backend en GET /:id). Si por
  // alguna razon no llego, se arma un fallback minimo con lo guardado en el item.
  const productoBase = item.producto || {
    id_unico: `${item.tipo}_${item.referencia_id}`,
    tipo: item.tipo,
    tipo_catalogo: 'producto',
    referencia_id: String(item.referencia_id),
    nombre: item.nombre || `(${item.tipo}) ${item.referencia_id}`,
    marca: item.marca || null,
    modelo: item.modelo || null,
    imagen_url: item.imagen_url || null,
    fuente: item.tipo === 'syscom_producto' ? 'syscom' : 'beep',
    precio_efectivo_mxn: congelado ? Number(item.precio_congelado_mxn) || 0 : Number(item.precio_actual_mxn) || 0,
    costo_mxn: congelado && item.costo_snapshot_mxn != null ? Number(item.costo_snapshot_mxn) : 0,
    margen_pct: congelado && item.margen_snapshot_pct != null ? Number(item.margen_snapshot_pct) : null,
    es_pactado: false, descuento_vs_lista_pct: 0, precio_lista_mxn: null,
    stock: null, unidad: 'pieza', tiene_variantes: false, variantes: [],
  };
  // SWITCH-GLOBAL Fase A: mostrar el COSTO LIVE (sin IVA, de la fuente) cuando exista, en
  // vez del snapshot viejo (que pudo quedar con IVA). El precio NO cambia. El margen se
  // recalcula al vuelo con el costo live y el precio mostrado.
  const costoLive = productoBase.costo_live_mxn;
  const usarLive = costoLive != null && Number(costoLive) > 0;
  const precioCard = Number(productoBase.precio_efectivo_mxn) || 0;
  const costoCard = usarLive ? Number(costoLive) : Number(productoBase.costo_mxn) || 0;
  const margenCard = (precioCard > 0 && costoCard > 0)
    ? Number(((precioCard - costoCard) / precioCard * 100).toFixed(2))
    : productoBase.margen_pct;
  // ALERTA-BAJO-COSTO: SIN COSTO no se enseña margen. Un "100%" calculado contra un costo de $0
  // (o el margen viejo del dia en que se congelo) es peor que ningun margen.
  const sinCosto = item.margen_estado && item.margen_estado.estado === 'sin_costo';
  const producto = sinCosto
    ? { ...productoBase, costo_mxn: 0, margen_pct: null }
    : usarLive
      ? { ...productoBase, costo_mxn: costoCard, margen_pct: margenCard }
      : productoBase;

  return (
    // CONTENEDOR de tarjeta UNICO y cerrado: todo (sello + producto + franja inferior) vive
    // dentro de este contorno, para que se entienda como UN solo bloque por producto.
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      background: colores.blanco,
      border: `1px solid ${colores.borde}`,
      borderRadius: 12,
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      padding: 8,
    }}>
      {/* SELLO de estado del congelado: chip en la esquina superior, dentro de la tarjeta. */}
      {vig && (
        <div style={{
          alignSelf: 'flex-start', marginBottom: 8,
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '4px 10px', borderRadius: 6,
          background: vig.selloBg, color: '#fff',
          fontSize: 11, fontWeight: 800, letterSpacing: 0.3, fontFamily: PP,
        }} title={`Congelado el ${fmtFecha(item.congelado_en)}`}>
          {vig.vencido
            ? <LucideIcons.AlertTriangle size={13} strokeWidth={2.5} />
            : <LucideIcons.Snowflake size={13} strokeWidth={2.5} />}
          {vig.sello}
        </div>
      )}
      {/* ALERTA-BAJO-COSTO: rojo / ambar / sello de perdida aceptada / sin costo. */}
      {item.margen_estado && item.margen_estado.estado !== 'ok' && (
        <div style={{ alignSelf: 'flex-start', marginBottom: 8, maxWidth: '100%' }}>
          <ChipMargen estado={item.margen_estado} onClick={onMargenClick} />
        </div>
      )}

      <TarjetaProducto
        producto={producto}
        modoCotizacion={modoCotizacion}
        onAgregarCarrito={(prod, cant) => onAgregarCarrito(cant, prod)}
        onVarianteChange={onGuardarVariante}
      />

      {/* FRANJA INFERIOR (fecha del congelado + acciones), separada por una linea divisoria
          sutil pero DENTRO de la misma tarjeta -> atada al producto de ARRIBA. */}
      <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${colores.borde}` }}>
        {fila && <EditorPrecioTarjeta fila={fila} onPrecio={onPrecio} onMargen={onMargen} />}
        {congelado && (
          <div style={{ fontSize: 11, lineHeight: 1.45, color: colores.grisClaro, marginBottom: 8 }}>
            <div>Congelado el {fmtFecha(item.congelado_en)}</div>
            {item.congelado_vence_en ? (
              <div style={{ color: vig ? vig.color : colores.grisClaro, fontWeight: 600 }}>
                Vence el {fmtFecha(item.congelado_vence_en)}
                {item.dias_restantes != null && (item.dias_restantes >= 0
                  ? ` (en ${item.dias_restantes} d)`
                  : ` (hace ${Math.abs(item.dias_restantes)} d)`)}
              </div>
            ) : (
              <div>Sin vencimiento (indefinido)</div>
            )}
            {vig && vig.vencido && (
              <div style={{ color: colores.rojo, marginTop: 2 }}>Al agregar al carrito se usa el precio de hoy.</div>
            )}
          </div>
        )}

        {/* Acciones: re-programar (SIEMPRE en congelados) + quitar de la lista. */}
        <div style={{ display: 'flex', gap: 6 }}>
          {congelado && (
            <button
              onClick={() => setReprogAbierto(true)}
              disabled={ocupado || agregandoCarrito}
              title="Re-programar: elegir precio (mantener o actualizar) y nueva vigencia"
              style={{
                flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                background: vig && vig.vencido ? colores.principal : colores.blanco,
                color: vig && vig.vencido ? colores.blanco : colores.oscuro,
                border: vig && vig.vencido ? 'none' : `1px solid ${colores.borde}`,
                padding: '7px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                cursor: ocupado ? 'default' : 'pointer', fontFamily: PP, opacity: ocupado ? 0.6 : 1,
              }}
            >
              <LucideIcons.RefreshCw size={14} /> Re-programar
            </button>
          )}
          <button
            onClick={onQuitar}
            disabled={ocupado || agregandoCarrito}
            title="Quitar de la lista"
            style={{
              flex: congelado ? '0 0 auto' : 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              background: colores.blanco, color: colores.rojo, border: `1px solid ${colores.borde}`,
              padding: '7px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600,
              cursor: ocupado ? 'default' : 'pointer', fontFamily: PP, opacity: ocupado ? 0.6 : 1,
            }}
          >
            <LucideIcons.Trash2 size={14} /> {congelado ? '' : 'Quitar'}
          </button>
        </div>
      </div>

      {reprogAbierto && (
        <ModalReprogramar
          item={item}
          onCerrar={() => setReprogAbierto(false)}
          onConfirmar={(opts) => { setReprogAbierto(false); onReprogramar(opts); }}
        />
      )}
    </div>
  );
}

// ============================================
// MINI-MODAL "Re-programar" — elegir precio + nueva vigencia
// ============================================
// PRECIO: mantener el congelado actual (no recalcula) o actualizar al de hoy.
// VIGENCIA: botones rapidos 30/90/180/365 + campo libre. Manda { dias_vigencia, mantener_precio }.
function ModalReprogramar({ item, onCerrar, onConfirmar }) {
  const congeladoActual = Number(item.precio_congelado_mxn);
  const precioHoy = item.precio_actual_mxn != null ? Number(item.precio_actual_mxn) : null;
  const [mantener, setMantener] = useState(true);
  const [dias, setDias] = useState(item.congelado_dias ? String(item.congelado_dias) : '30');

  const diasNum = Math.trunc(Number(dias));
  const diasValido = Number.isFinite(diasNum) && diasNum > 0;
  const fechaVence = (() => {
    if (!diasValido) return '—';
    const f = new Date(); f.setDate(f.getDate() + diasNum);
    return f.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  })();

  const fmtM = (n) => n == null || !Number.isFinite(Number(n)) ? '—'
    : Number(n).toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const opcionPrecio = (val, titulo, monto, sub) => (
    <button
      type="button"
      onClick={() => setMantener(val)}
      style={{
        flex: 1, textAlign: 'left', cursor: 'pointer', fontFamily: PP,
        border: `2px solid ${mantener === val ? colores.principal : colores.borde}`,
        background: mantener === val ? colores.principal + '11' : colores.blanco,
        borderRadius: 10, padding: '10px 12px',
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: colores.oscuro }}>{titulo}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: colores.oscuro, marginTop: 2 }}>{fmtM(monto)}</div>
      {sub && <div style={{ fontSize: 10, color: colores.grisClaro, marginTop: 2 }}>{sub}</div>}
    </button>
  );

  return (
    <ModalOverlay onClose={onCerrar}>
      <h3 style={{ margin: '0 0 4px', fontSize: 18, color: colores.oscuro, fontWeight: 700 }}>Re-programar precio</h3>
      <div style={{ fontSize: 12, color: colores.grisClaro, marginBottom: 16 }}>{item.nombre}</div>

      <div style={{ ...estiloLabel, marginBottom: 8 }}>Precio</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {opcionPrecio(true, 'Mantener congelado', congeladoActual, 'No cambia el precio')}
        {opcionPrecio(false, 'Actualizar a hoy', precioHoy, precioHoy == null ? 'No disponible' : 'Precio actual del sistema')}
      </div>

      <div style={{ ...estiloLabel, marginBottom: 8 }}>Nueva vigencia</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {[30, 90, 180, 365].map(n => (
          <button
            key={n}
            type="button"
            onClick={() => setDias(String(n))}
            style={{
              cursor: 'pointer', fontFamily: PP, fontSize: 13, fontWeight: 700,
              border: `1px solid ${diasNum === n ? colores.principal : colores.borde}`,
              background: diasNum === n ? colores.principal : colores.blanco,
              color: diasNum === n ? colores.blanco : colores.gris,
              borderRadius: 8, padding: '8px 14px',
            }}
          >{n} d</button>
        ))}
        <input
          type="number" min="1" step="1" value={dias}
          onChange={(e) => setDias(e.target.value)}
          placeholder="dias"
          style={{ ...estiloInput, marginBottom: 0, width: 90 }}
        />
      </div>
      <div style={{ fontSize: 12, color: colores.grisClaro, marginBottom: 18 }}>
        Vence el <strong style={{ color: colores.oscuro }}>{fechaVence}</strong>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onCerrar} style={estiloBotonSecundario}>Cancelar</button>
        <button
          onClick={() => onConfirmar({ dias_vigencia: diasValido ? diasNum : 30, mantener_precio: mantener })}
          disabled={!diasValido && dias !== ''}
          style={estiloBotonPrimario}
        >
          <LucideIcons.RefreshCw size={15} /> Re-programar
        </button>
      </div>
    </ModalOverlay>
  );
}

// ============================================
// VISTA TABLA — tipo cotizador, margen/precio EDITABLES en vivo + Guardar
// ============================================
// Replica la tabla del precotizador (reusa InputPrecio/InputMargen + round2/colorMargen).
// Edicion LIBRE en memoria (no toca BD); "Guardar" congela el precio/margen editado de las
// lineas que cambiaron (endpoint congelar solo_precio); "Descartar" revierte.
// Etiqueta amigable del origen/tipo (el dato crudo es 'beep_fijo', 'syscom_producto', etc.).
const TIPO_LABEL = {
  syscom_producto: 'Syscom',
  beep_producto: 'Beep',
  beep_cable: 'Cableado',
  beep_servicio: 'Servicio',
  beep_instalacion: 'Instalacion',
  beep_fijo: 'Concepto',
};

function filaDeItem(it) {
  const congelado = it.congelado === true;
  const prod = it.producto || {};
  // COSTO LIVE (sin IVA, de la fuente) si existe; si no, fallback al snapshot / al del producto.
  // ALERTA-BAJO-COSTO: si el servidor dice que la linea NO tiene costo de hoy, no se cae al costo
  // viejo del snapshot: sin costo, sin margen.
  const sinCosto = it.margen_estado && it.margen_estado.estado === 'sin_costo';
  const costo = sinCosto ? 0 : (prod.costo_live_mxn != null && Number(prod.costo_live_mxn) > 0)
    ? Number(prod.costo_live_mxn)
    : (it.costo_snapshot_mxn != null ? Number(it.costo_snapshot_mxn)
      : (prod.costo_mxn != null ? Number(prod.costo_mxn) : 0));
  const precioLista = prod.precio_lista_mxn != null ? Number(prod.precio_lista_mxn) : null;
  const precio = congelado ? (Number(it.precio_congelado_mxn) || 0)
    : (it.precio_actual_mxn != null ? Number(it.precio_actual_mxn) : (Number(prod.precio_efectivo_mxn) || 0));
  // Margen se recalcula al vuelo con el costo LIVE y el precio (no el snapshot guardado).
  const margen = margenDePrecioCosto(precio, costo);
  return {
    id: it.id, item: it,
    nombre: it.nombre || `(${it.tipo}) ${it.referencia_id}`,
    modelo: prod.modelo || it.modelo || null,
    imagen_url: it.imagen_url || prod.imagen_url || null,
    // Origen/tipo en etiqueta AMIGABLE (no el id tecnico crudo tipo "beep_fijo").
    tipoTxt: TIPO_LABEL[it.tipo] || 'Producto',
    cantidad: 1,
    costo, precioLista,
    precio, margen,
    precio0: precio, margen0: margen, // semillas para detectar cambios / descartar
  };
}

// PRECIO-TARJETAS (2026-10-12): el estado de edicion, sacado de VistaTablaColeccion SIN cambiar sus
// reglas, para que Tarjetas y Tabla editen el MISMO precio con el MISMO control (InputPrecio) y
// guarden por el MISMO camino. Antes solo la Tabla podia editar.
function useEdicionPrecios(items) {
  const [filas, setFilas] = useState(() => items.map(filaDeItem));
  // Re-sembrar cuando cambian los items (tras guardar/quitar/recargar).
  useEffect(() => { setFilas(items.map(filaDeItem)); }, [items]);

  const setFila = (id, cambios) => setFilas(arr => arr.map(f => f.id === id ? { ...f, ...cambios } : f));

  // Margen -> precio: precio = costo/(1 - m/100), m en [0,95] (identico al cotizador).
  const onMargen = (id, valor) => setFilas(arr => arr.map(f => {
    if (f.id !== id) return f;
    if ((Number(f.costo) || 0) <= 0) return { ...f, margen: valor };
    const m = Math.min(95, Math.max(0, Number(valor) || 0));
    return { ...f, margen: valor, precio: round2((Number(f.costo) || 0) / (1 - m / 100)) };
  }));
  // Precio -> margen: margen = (precio - costo)/precio*100.
  const onPrecio = (id, valor) => setFilas(arr => arr.map(f => {
    if (f.id !== id) return f;
    const precio = Number(valor) || 0;
    const costo = Number(f.costo) || 0;
    const margen = (precio > 0 && costo > 0) ? round2(((precio - costo) / precio) * 100) : f.margen;
    return { ...f, precio: valor, margen };
  }));

  const cambios = filas.filter(esCambio);
  return { filas, setFila, onMargen, onPrecio, cambios, hayCambios: cambios.length > 0, descartar: () => setFilas(items.map(filaDeItem)) };
}
const esCambio = (f) => round2(f.precio) !== round2(f.precio0) || Number(f.margen) !== Number(f.margen0);

// Barra Guardar / Descartar, comun a Tarjetas y Tabla.
function BarraCambiosPrecio({ edicion, guardando, onGuardar, flotante = false }) {
  const { cambios, hayCambios } = edicion;
  // flotante: pegada al pie de la pantalla (vista Tarjetas). Botones a la IZQUIERDA para que el
  // boton redondo del asistente (abajo a la derecha) no los tape.
  const caja = flotante
    ? { position: 'sticky', bottom: 8, zIndex: 5, marginTop: 14, padding: 10, background: colores.blanco, border: `1px solid ${colores.borde}`, borderRadius: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', justifyContent: 'flex-start' }
    : { marginBottom: 10, justifyContent: 'flex-end' };
  return (
    // Barra de acciones: etiqueta aclaratoria + cambios + Guardar / Descartar.
    // La edicion es de administracion interna de costo/margen: SIEMPRE en MXN base
    // SIN IVA (no reacciona al toggle global, a proposito).
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', ...caja }}>
      {!flotante && (
        <span style={{ marginRight: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: colores.grisClaro, fontWeight: 600 }}>
          <LucideIcons.Info size={14} /> Precios editables sin IVA · MXN
        </span>
      )}
      {hayCambios && <span style={{ fontSize: 12, color: '#c47d12', fontWeight: 600 }}>{cambios.length} cambio{cambios.length === 1 ? '' : 's'} sin guardar</span>}
      <button
        onClick={edicion.descartar}
        disabled={!hayCambios || guardando}
        style={{ ...estiloBotonSecundario, opacity: (!hayCambios || guardando) ? 0.5 : 1 }}
      >Descartar</button>
      <button
        onClick={() => onGuardar(cambios.map(c => ({ id: c.id, nombre: c.nombre, precio: round2(c.precio), margen: Number(c.margen), costo: round2(c.costo) })))}
        disabled={!hayCambios || guardando}
        style={{ ...estiloBotonPrimario, background: hayCambios ? colores.verde : colores.principal, opacity: (!hayCambios || guardando) ? 0.6 : 1 }}
      >
        <LucideIcons.Save size={15} /> {guardando ? 'Guardando...' : 'Guardar'}
      </button>
    </div>
  );
}

// Editor de precio dentro de la TARJETA: los mismos InputMargen/InputPrecio de la Tabla, sobre la
// misma fila de useEdicionPrecios.
function EditorPrecioTarjeta({ fila, onPrecio, onMargen }) {
  const cambiado = esCambio(fila);
  const campo = { padding: '6px 8px', border: `1px solid ${cambiado ? '#c47d12' : colores.borde}`, borderRadius: 6, fontSize: 14, fontFamily: PP, color: colores.oscuro, textAlign: 'right', outline: 'none', background: colores.fondo, minHeight: 36, boxSizing: 'border-box' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: colores.gris, flex: '1 1 100%' }}>
        Precio sin IVA{cambiado && <span style={{ color: '#c47d12' }}> · sin guardar</span>}
      </span>
      <InputPrecio valorMXN={fila.precio} onCommit={(v) => onPrecio(fila.id, v)} style={{ ...campo, flex: '1 1 110px', minWidth: 0 }} />
      <InputMargen valor={fila.margen} costo={fila.costo} onChange={(v) => onMargen(fila.id, v)} style={{ ...campo, width: 56 }} />
    </div>
  );
}

// Lo que el buscador mira de cada producto de la lista: nombre, marca y modelo.
function candidatoDeItem(it) {
  const prod = it.producto || {};
  return { id: it.id, titulo: prod.nombre || it.nombre || '', marca: prod.marca || it.marca || '', modelo: prod.modelo || it.modelo || '' };
}

function VistaTablaColeccion({ edicion, idsOrden, onQuitar, onAgregarCarrito, onMargenClick, agregandoCarritoId, modoCotizacion = false }) {
  // RESPONSIVE A-2: la tabla es ancha (minWidth 760). Con ancho real (>=1100) se ve la
  // tabla; en angosto (celular, tablet y PC VERTICAL) se apilan TARJETAS editables (mismos
  // inputs de margen/precio/cantidad y mismas acciones) -> sin scroll horizontal.
  const ancho = useAnchoViewport();
  const compacto = ancho < 1100;
  const { setFila, onMargen, onPrecio } = edicion;
  // Solo las filas que deja el buscador, en su orden de relevancia. Lo editado y no visible se
  // sigue guardando (los cambios se cuentan sobre TODAS las filas).
  const porId = new Map(edicion.filas.map(f => [f.id, f]));
  const filas = (idsOrden || []).map(id => porId.get(id)).filter(Boolean);

  const totales = filas.reduce((acc, f) => {
    const cant = Math.max(1, Math.round(Number(f.cantidad) || 1));
    const precio = Number(f.precio) || 0;
    const costo = Number(f.costo) || 0;
    acc.sub += precio * cant;
    acc.util += (precio - costo) * cant;
    return acc;
  }, { sub: 0, util: 0 });

  const fmtM = (n) => Number(n || 0).toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const th = { fontSize: 10, fontWeight: 700, color: colores.grisClaro, textTransform: 'uppercase', letterSpacing: 0.4, padding: '8px 10px', borderBottom: `1px solid ${colores.borde}`, whiteSpace: 'nowrap' };
  // Separador de fila mas visible (1px solido, un tono mas marcado que el borde base
  // pero sin verse cargado), consistente en todas las filas.
  const SEP_FILA = '#cfd6dd';
  const td = { fontSize: 13, color: colores.oscuro, padding: '12px 10px', borderBottom: `1px solid ${SEP_FILA}`, verticalAlign: 'middle' };
  const campo = { width: 80, padding: '6px 8px', border: `1px solid ${colores.borde}`, borderRadius: 6, fontSize: 13, fontFamily: PP, color: colores.oscuro, textAlign: 'right', outline: 'none', background: colores.fondo };

  return (
    <div>
      {/* ANGOSTO: tarjetas editables apiladas (sin scroll horizontal) */}
      {compacto && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filas.map(f => {
            const cant = Math.max(1, Math.round(Number(f.cantidad) || 1));
            const precio = Number(f.precio) || 0;
            const costo = Number(f.costo) || 0;
            const sub = precio * cant;
            const util = (precio - costo) * cant;
            return (
              <div key={f.id} style={{ background: colores.blanco, border: `1px solid ${colores.borde}`, borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {/* cabecera */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ width: 56, height: 56, borderRadius: 8, flexShrink: 0, overflow: 'hidden', background: colores.fondo, border: `1px solid ${colores.borde}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    {f.imagen_url
                      ? <img src={f.imagen_url} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                      : <LucideIcons.Package size={24} color={colores.grisClaro} />}
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: colores.oscuro, lineHeight: 1.3, wordBreak: 'break-word' }}>{f.nombre}</div>
                    {f.modelo && <div style={{ fontSize: 12, color: '#6d28d9', fontWeight: 800, fontFamily: 'monospace', marginTop: 1 }}>{f.modelo}</div>}
                    <div style={{ fontSize: 11, color: colores.grisClaro }}>{f.tipoTxt}</div>
                    {f.item.margen_estado && f.item.margen_estado.estado !== 'ok' && (
                      <div style={{ marginTop: 4 }}><ChipMargen estado={f.item.margen_estado} compacto onClick={() => onMargenClick && onMargenClick(f.item)} /></div>
                    )}
                  </div>
                </div>
                {/* lista + costo (solo lectura) */}
                <div style={{ display: 'flex', gap: 16, fontSize: 12, color: colores.grisClaro, flexWrap: 'wrap' }}>
                  <span>Lista: {f.precioLista != null ? fmtM(f.precioLista) : '—'}</span>
                  <span>Costo: {costo > 0 ? fmtM(costo) : '—'}</span>
                </div>
                {/* controles editables */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ fontSize: 12, color: colores.gris, fontWeight: 600 }}>Cantidad</span>
                  <input type="number" min="1" step="1" value={f.cantidad} onChange={(e) => setFila(f.id, { cantidad: e.target.value })} style={{ ...campo, width: 80, textAlign: 'center' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ fontSize: 12, color: colores.gris, fontWeight: 600 }}>Margen %</span>
                  <InputMargen valor={f.margen} costo={f.costo} onChange={(v) => onMargen(f.id, v)} style={{ ...campo, width: 90 }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ fontSize: 12, color: colores.gris, fontWeight: 600 }}>Precio venta</span>
                  <InputPrecio valorMXN={f.precio} onCommit={(v) => onPrecio(f.id, v)} style={{ ...campo, width: 110 }} />
                </div>
                {/* pie: subtotal/utilidad + acciones */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, borderTop: `1px solid ${colores.borde}`, paddingTop: 8, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: 15, fontWeight: 800, color: colores.oscuro }}>{fmtM(sub)}</span>
                    <span style={{ fontSize: 11, color: util >= 0 ? colores.verde : colores.rojo, fontWeight: 700 }}>Utilidad {fmtM(util)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => onAgregarCarrito && onAgregarCarrito(f.item, cant)}
                      disabled={agregandoCarritoId === f.id}
                      title={modoCotizacion ? 'Agregar a la cotizacion (precio congelado)' : 'Agregar al carrito (precio congelado)'}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: colores.principal, color: '#fff', border: 'none', padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, fontFamily: PP, cursor: agregandoCarritoId === f.id ? 'default' : 'pointer', opacity: agregandoCarritoId === f.id ? 0.6 : 1 }}
                    >
                      <LucideIcons.ShoppingCart size={14} /> {modoCotizacion ? 'A la cotizacion' : 'Agregar'}
                    </button>
                    <button onClick={() => onQuitar(f.item)} title="Quitar de la lista" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: colores.blanco, color: colores.rojo, border: `1px solid ${colores.borde}`, padding: '8px 10px', borderRadius: 8, cursor: 'pointer' }}>
                      <LucideIcons.Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {/* Totales (modo tarjetas) */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: colores.fondo, border: `1px solid ${colores.borde}`, borderRadius: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: colores.gris }}>Totales</span>
            <span style={{ fontSize: 15, fontWeight: 800, color: colores.oscuro }}>
              {fmtM(totales.sub)} · <span style={{ color: totales.util >= 0 ? colores.verde : colores.rojo }}>util {fmtM(totales.util)}</span>
            </span>
          </div>
        </div>
      )}

      {!compacto && (
      <div style={{ background: colores.blanco, border: `1px solid ${colores.borde}`, borderRadius: 12, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: PP, minWidth: 760 }}>
          <thead>
            <tr style={{ background: colores.fondo }}>
              <th style={{ ...th, textAlign: 'left' }}>Articulo</th>
              <th style={{ ...th, textAlign: 'center' }}>Cantidad</th>
              <th style={{ ...th, textAlign: 'right' }}>Lista</th>
              <th style={{ ...th, textAlign: 'right' }}>Costo</th>
              <th style={{ ...th, textAlign: 'right' }}>Margen %</th>
              <th style={{ ...th, textAlign: 'right' }}>Precio venta</th>
              <th style={{ ...th, textAlign: 'right' }}>Subtotal</th>
              <th style={{ ...th, textAlign: 'right' }}>Utilidad</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {filas.map(f => {
              const cant = Math.max(1, Math.round(Number(f.cantidad) || 1));
              const precio = Number(f.precio) || 0;
              const costo = Number(f.costo) || 0;
              const sub = precio * cant;
              const util = (precio - costo) * cant;
              return (
                <tr key={f.id}>
                  <td style={{ ...td, minWidth: 220 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ width: 64, height: 64, borderRadius: 8, flexShrink: 0, overflow: 'hidden', background: colores.fondo, border: `1px solid ${colores.borde}`, padding: 5, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        {f.imagen_url
                          ? <img src={f.imagen_url} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                          : <LucideIcons.Package size={26} color={colores.grisClaro} />}
                      </span>
                      <div style={{ minWidth: 0, maxWidth: 320 }}>
                        {/* Nombre COMPLETO (wrap, sin truncar con "..."). */}
                        <div style={{ fontWeight: 600, lineHeight: 1.3, whiteSpace: 'normal', wordBreak: 'break-word' }}>{f.nombre}</div>
                        {f.modelo && (
                          <div style={{ fontSize: 13, color: '#6d28d9', fontWeight: 800, fontFamily: 'monospace', marginTop: 1 }}>{f.modelo}</div>
                        )}
                        <div style={{ fontSize: 11, color: colores.grisClaro }}>{f.tipoTxt}</div>
                        {f.item.margen_estado && f.item.margen_estado.estado !== 'ok' && (
                          <div style={{ marginTop: 4 }}><ChipMargen estado={f.item.margen_estado} compacto onClick={() => onMargenClick && onMargenClick(f.item)} /></div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <input type="number" min="1" step="1" value={f.cantidad}
                      onChange={(e) => setFila(f.id, { cantidad: e.target.value })}
                      style={{ ...campo, width: 60, textAlign: 'center' }} />
                  </td>
                  <td style={{ ...td, textAlign: 'right', color: colores.grisClaro, whiteSpace: 'nowrap' }}>{f.precioLista != null ? fmtM(f.precioLista) : '—'}</td>
                  <td style={{ ...td, textAlign: 'right', color: colores.grisClaro, whiteSpace: 'nowrap' }}>{costo > 0 ? fmtM(costo) : '—'}</td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <InputMargen valor={f.margen} costo={f.costo} onChange={(v) => onMargen(f.id, v)} style={{ ...campo, width: 60 }} />
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <InputPrecio valorMXN={f.precio} onCommit={(v) => onPrecio(f.id, v)} style={{ ...campo, width: 96 }} />
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtM(sub)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap', color: util >= 0 ? colores.verde : colores.rojo }}>{fmtM(util)}</td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      {/* Agregar al carrito: MISMA funcion que la vista Tarjetas (respeta el
                          precio congelado). Usa la cantidad de la fila. */}
                      <button
                        onClick={() => onAgregarCarrito && onAgregarCarrito(f.item, cant)}
                        disabled={agregandoCarritoId === f.id}
                        title={modoCotizacion ? 'Agregar a la cotizacion (precio congelado)' : 'Agregar al carrito (precio congelado)'}
                        style={{ background: 'transparent', border: 'none', cursor: agregandoCarritoId === f.id ? 'default' : 'pointer', color: colores.principal, padding: 6, opacity: agregandoCarritoId === f.id ? 0.5 : 1 }}>
                        <LucideIcons.ShoppingCart size={16} />
                      </button>
                      <button onClick={() => onQuitar(f.item)} title="Quitar de la lista"
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: colores.rojo, padding: 6 }}>
                        <LucideIcons.Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: colores.fondo }}>
              <td style={{ ...td, fontWeight: 700 }} colSpan={6}>Totales</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 800, whiteSpace: 'nowrap' }}>{fmtM(totales.sub)}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 800, whiteSpace: 'nowrap', color: totales.util >= 0 ? colores.verde : colores.rojo }}>{fmtM(totales.util)}</td>
              <td style={td}></td>
            </tr>
          </tfoot>
        </table>
      </div>
      )}
      <div style={{ fontSize: 11, color: colores.grisClaro, marginTop: 8 }}>
        Edita el margen o el precio (se recalculan entre si). La cantidad es solo para ver el subtotal/utilidad; no se guarda. <strong>Guardar</strong> congela el precio editado de cada linea cambiada.
      </div>
    </div>
  );
}

// ============================================
// BUSCADOR EMBEBIDO — agregar productos a la lista (reusa /api/tienda/buscar)
// ============================================
// Campo de busqueda + resultados compactos + "Agregar". Si la lista es CONGELADA, el
// item se congela con el precio del momento (backend) y la vigencia de la lista.
function BuscadorAgregar({ coleccion, onAgregado, onError, onCerrar }) {
  const [q, setQ] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [agregandoId, setAgregandoId] = useState(null);
  const [busco, setBusco] = useState(false);

  const buscar = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const term = q.trim();
    if (!term) return;
    setBuscando(true);
    setBusco(true);
    try {
      const r = await fetchAuth(`${API_BASE_CLI}/api/tienda/buscar?q=${encodeURIComponent(term)}&limit=12`);
      const d = await r.json();
      setResultados(d.ok ? (d.productos || []) : []);
      if (!d.ok) onError(d.error || 'No se pudo buscar');
    } catch (err) {
      if (err.code !== 'SESION_EXPIRADA') onError('Error al buscar: ' + (err.message || ''));
    } finally {
      setBuscando(false);
    }
  };

  const agregar = async (p) => {
    setAgregandoId(p.id_unico);
    try {
      const body = {
        tipo: p.tipo,
        referencia_id: p.referencia_id,
        nombre: p.nombre,
        imagen_url: p.imagen_url || null,
        marca: p.marca || null,
        modelo: p.modelo || null,
        // Si la lista es congelada, congelar al precio de hoy con la vigencia de la lista.
        congelar: coleccion.congelada === true,
        precio_congelar_mxn: Number(p.precio_efectivo_mxn) || null,
        dias_vigencia: coleccion.congelado_dias || null,
      };
      const r = await fetchAuth(`${API_BASE_CLI}/api/colecciones/${coleccion.id}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) { onError(d.error || 'No se pudo agregar'); return; }
      onAgregado(p.nombre);
    } catch (err) {
      if (err.code !== 'SESION_EXPIRADA') onError('Error al agregar: ' + (err.message || ''));
    } finally {
      setAgregandoId(null);
    }
  };

  return (
    <div style={{
      background: colores.blanco, border: `1px solid ${colores.borde}`, borderRadius: 12,
      padding: 14, marginBottom: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: colores.oscuro }}>
          Agregar productos {coleccion.congelada ? '(se congelan al precio de hoy)' : ''}
        </div>
        <button onClick={onCerrar} title="Cerrar" style={estiloBotonIcono}>
          <LucideIcons.X size={16} />
        </button>
      </div>

      <form onSubmit={buscar} style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar en el catalogo (Syscom + Beep)..."
          style={{ ...estiloInput, marginBottom: 0, flex: 1 }}
        />
        <button type="submit" disabled={buscando} style={estiloBotonPrimario}>
          <LucideIcons.Search size={15} /> {buscando ? 'Buscando...' : 'Buscar'}
        </button>
      </form>

      {busco && !buscando && resultados.length === 0 && (
        <div style={{ fontSize: 13, color: colores.grisClaro, padding: '8px 2px' }}>Sin resultados.</div>
      )}

      {resultados.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 360, overflowY: 'auto' }}>
          {resultados.map(p => (
            <div key={p.id_unico} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              border: `1px solid ${colores.borde}`, borderRadius: 8, padding: '8px 10px',
            }}>
              <span style={{
                width: 40, height: 40, borderRadius: 6, flexShrink: 0, overflow: 'hidden',
                background: colores.fondo, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {p.imagen_url
                  ? <img src={p.imagen_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                  : <LucideIcons.Package size={18} color={colores.grisClaro} />}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: colores.oscuro, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.nombre}</div>
                <div style={{ fontSize: 11, color: colores.grisClaro }}>
                  {[p.marca, p.modelo].filter(Boolean).join(' · ') || (p.fuente === 'beep' ? 'Beep' : 'Syscom')}
                  {Number(p.precio_efectivo_mxn) > 0 && ` · ${Number(p.precio_efectivo_mxn).toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </div>
              </div>
              <button
                onClick={() => agregar(p)}
                disabled={agregandoId === p.id_unico}
                style={{ ...estiloBotonPrimario, padding: '7px 12px', whiteSpace: 'nowrap' }}
              >
                <LucideIcons.Plus size={14} /> {agregandoId === p.id_unico ? 'Agregando...' : 'Agregar'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// Overlay de modal (mismo patron que CatalogoEstados)
// ============================================
function ModalOverlay({ onClose, children }) {
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400 }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        background: colores.blanco, borderRadius: 12, padding: 24, zIndex: 401,
        width: 'min(94vw, 460px)', maxHeight: 'calc(var(--beep-vh) * 90)', overflowY: 'auto',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)', fontFamily: PP,
      }}>
        {children}
      </div>
    </>
  );
}

// ============================================
// Estilos
// ============================================
const estiloLabel = { display: 'block', fontSize: 12, fontWeight: 600, color: colores.gris, marginBottom: 5 };
const estiloInput = {
  width: '100%', boxSizing: 'border-box', padding: '9px 11px',
  border: `1px solid ${colores.borde}`, borderRadius: 8, fontSize: 14,
  fontFamily: PP, color: colores.gris, marginBottom: 14, outline: 'none',
};
const estiloBotonPrimario = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  background: colores.principal, color: colores.blanco, border: 'none',
  padding: '9px 16px', borderRadius: 8, fontSize: 14, fontWeight: 600,
  cursor: 'pointer', fontFamily: PP,
};
const estiloBotonPrimarioGrande = { ...estiloBotonPrimario, padding: '10px 16px' };
const estiloBotonSecundario = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  background: colores.blanco, color: colores.gris, border: `1px solid ${colores.borde}`,
  padding: '9px 14px', borderRadius: 8, fontSize: 14, fontWeight: 600,
  cursor: 'pointer', fontFamily: PP,
};
const estiloBotonIcono = {
  background: 'transparent', border: `1px solid ${colores.borde}`, cursor: 'pointer',
  padding: 8, borderRadius: 8, color: colores.gris,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};
const bannerError = {
  background: '#fdecec', color: '#9b2c2c', border: '1px solid #f5b7b7',
  padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12,
};
const bannerExito = {
  background: '#e7f8f1', color: '#1d7a5e', border: '1px solid #a7e8d2',
  padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12,
};
